import type { FC } from 'react';

import { useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { useEvaultAddon } from 'hooks';
import { ROUTES } from 'routes';
import { Modal, StatusChip } from 'views/design-system';

import { money } from './derive';

/**
 * The add-on upsell (§8e, first half).
 *
 * It is the screen every `unentitled` outcome lands on — the head's Create
 * button when the band is up, the unentitled `Blank`'s CTA, and any 403
 * `EVAULT_NOT_ENTITLED` the API answers with. A paywall is not an error, so it
 * is never a toast (R10).
 *
 * THE PRICES ARE THE SERVER'S. `GET /evault-addons` carries `price` and
 * `transferPrice`; nothing here hardcodes either, because a figure baked into a
 * modal is a figure that keeps being shown after the backend changes it. Until
 * the status resolves the line reads `…` rather than a placeholder number, and
 * if it resolves to nothing the line is dropped entirely — quoting `$0.00` at
 * someone about to agree to a charge would be worse than quoting nothing.
 *
 * MOUNT IT ONLY WHEN OPEN. `useEvaultAddon` fetches on mount, and this modal is
 * shown on a minority of visits; rendering it permanently would put a billing
 * request on every load of a page that does not need one.
 *
 * "Add to plan" NAVIGATES, it does not buy. The purchase is a real charge on the
 * default payment method and lives on `/billing`, behind that page's own
 * confirmation. `navigate` rather than `window.location.assign`, which is what
 * the component upstream used: a full reload to reach another route inside the
 * same SPA (§2.11).
 */
export interface IAddonUpsellModalProps {
	open: boolean;
	onClose: () => void;
}

export const AddonUpsellModal: FC<IAddonUpsellModalProps> = ({ open, onClose }) => {
	const navigate = useNavigate();
	const { status, isLoaded } = useEvaultAddon();

	const addToPlan = useCallback(() => {
		onClose();
		navigate(`${ROUTES.BILLING}#billing-evault-addon`);
	}, [navigate, onClose]);

	/* The one refusal the button itself has to explain: the add-on attaches to a
	   subscription, so there is nothing for it to attach to yet. Disabled with
	   the reason on it rather than hidden — an absent button is a question, a
	   disabled one with a title is an answer. */
	const noSubscription = status?.reason === 'no-subscription';

	return (
		<Modal
			open={open}
			title="eVault"
			size="narrow"
			className="evault-modal"
			onClose={onClose}
			footer={
				<>
					<button className="dbtn" type="button" onClick={onClose}>
						Not now
					</button>
					<button
						className="dbtn pri"
						type="button"
						disabled={noSubscription}
						title={
							noSubscription
								? 'Subscribe to a plan first — the add-on attaches to your existing subscription.'
								: undefined
						}
						onClick={addToPlan}
					>
						Add to plan
					</button>
				</>
			}
		>
			<div className="upsell">
				<div className="uhead">
					<span className="utile" aria-hidden="true">
						&#9672;
					</span>
					<span className="uname">eVault</span>
					<StatusChip verdict="strt">NEW</StatusChip>
				</div>

				<div className="udesc">
					Securely store the authoritative original of every signed document in an
					encrypted vault with a cryptographic seal and tamper-evident audit
					trail.
				</div>

				{(!isLoaded || !!status) && (
					<div className="uprice">
						<b>{isLoaded && status ? `$${money(status.price)}` : '…'} / month</b>
						<span>
							plus{' '}
							{isLoaded && status ? `$${money(status.transferPrice)}` : '…'} per
							transferred document
						</span>
					</div>
				)}

				{noSubscription && (
					<div className="uhint">
						Subscribe to a plan first — the add-on attaches to your existing
						subscription.
					</div>
				)}
			</div>
		</Modal>
	);
};

export default AddonUpsellModal;
